/**
 * When a meeting row says it happens, in words a person plans around: "Starting now", "In 10 minutes",
 * "Tomorrow, 4:00 PM", then a plain date past that.
 *
 * Import-free on purpose — `meetings-channel.ts` reaches react-query and `api-client`, which a node-env
 * jest suite here must not load (see the orion-web CLAUDE.md tests section).
 *
 * Returns `startingNow` rather than a string for the one case `Intl` has no phrase for, so the caller
 * supplies it from the translation bundle. Everything else comes out of `Intl` already localized.
 */
export type MeetingWhen = { kind: 'startingNow' } | { kind: 'text'; text: string };

const MINUTE = 60_000;
/** A meeting that began this long ago still reads "Starting now" — people join a few minutes late. */
const STARTING_WINDOW_BEFORE = 5 * MINUTE;
/** …and one this close ahead does too: "In 1 minute" is noise, not information. */
const STARTING_WINDOW_AHEAD = MINUTE;
const HOUR = 60 * MINUTE;

function capitalizeFirst(text: string, locale?: string): string {
  return text ? text.charAt(0).toLocaleUpperCase(locale) + text.slice(1) : text;
}

function startOfDay(ms: number): number {
  const day = new Date(ms);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

export function meetingWhen(startsAt: string, now: number = Date.now(), locale?: string): MeetingWhen {
  const start = Date.parse(startsAt);
  if (Number.isNaN(start)) return { kind: 'text', text: '' };
  const until = start - now;

  if (until <= STARTING_WINDOW_AHEAD && until >= -STARTING_WINDOW_BEFORE) return { kind: 'startingNow' };

  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (until > 0 && until < HOUR) {
    return { kind: 'text', text: capitalizeFirst(relative.format(Math.round(until / MINUTE), 'minute'), locale) };
  }

  const time = new Date(start).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
  const days = Math.round((startOfDay(start) - startOfDay(now)) / (24 * HOUR));
  if (Math.abs(days) <= 1) {
    return { kind: 'text', text: `${capitalizeFirst(relative.format(days, 'day'), locale)}, ${time}` };
  }
  return {
    kind: 'text',
    text: new Date(start).toLocaleString(locale, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }),
  };
}

/**
 * Initials for an attendee the calendar may know only by address. A display name takes the first letter
 * of its first two words; an address takes its local part split on `.`, `_` and `-`, so
 * `a.rossi@host` reads "AR" rather than "A@".
 */
export function attendeeInitials(nameOrEmail: string): string {
  const source = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0]!.replace(/[._-]+/g, ' ') : nameOrEmail;
  const words = source.split(/\s+/).filter(Boolean);
  const initials = (words[0]?.[0] ?? '') + (words[1]?.[0] ?? '');
  return (initials || nameOrEmail.slice(0, 1)).toUpperCase();
}
